import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { CircleHelp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, Tab } from '@/components/crm-ui/Tab';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatUsageLimit } from '@/lib/billing/billingOverview';

export type BillingTopTab = {
  value: string;
  label: string;
  count?: number;
};

export function BillingTopTabs({
  tabs,
  activeTab,
  onTabChange,
}: {
  tabs: BillingTopTab[];
  activeTab: string;
  onTabChange: (value: string) => void;
}) {
  return (
    <div
      className="border-b border-[#EAECF0] bg-white px-[15px] pt-3"
      data-testid="billing-top-tabs"
    >
      <Tabs
        variant="underline"
        value={activeTab}
        onChange={onTabChange}
        ariaLabel="Billing sections"
        className="flex-nowrap overflow-x-auto overflow-y-hidden"
      >
        {tabs.map((t) => (
          <Tab key={t.value} value={t.value} count={t.count}>
            {t.label}
          </Tab>
        ))}
      </Tabs>
    </div>
  );
}

export function BillingSectionCard({
  children,
  className,
  testId,
}: {
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'overflow-hidden rounded-xl border border-[#EAECF0] bg-white',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function BillingOverviewShell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

export function BillingSectionHeader({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3 px-5 py-4 sm:px-6', className)}>
      <div className="min-w-0">
        <h3 className="text-[15px] font-semibold text-[#101828]">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-[13px] text-[#667085]">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function BillingStatusPill({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'neutral' | 'danger';
  children: ReactNode;
}) {
  // Shape, size and dot follow the app-wide status pill (commerce-v2 StatusPill):
  // a squared-off chip, not a lozenge, and the dot takes the label's own colour.
  const wrap = {
    success: 'bg-brand-soft text-brand-cta',
    warning: 'bg-[#FFFAEB] text-[#B54708]',
    neutral: 'bg-[#F2F4F7] text-[#475467]',
    danger: 'bg-[#FEF3F2] text-[#B42318]',
  }[tone];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-[3px] text-[11.5px] font-semibold',
        wrap,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {children}
    </span>
  );
}

export function BillingUsageTile({
  icon,
  label,
  used,
  limitLabel,
  percent,
}: {
  icon: ReactNode;
  label: string;
  used: string;
  limitLabel: string;
  percent: number;
}) {
  const barWidth = limitLabel === '∞' || limitLabel === 'Unlimited' ? 0 : percent;
  const overLimit = barWidth >= 100;

  return (
    <div className="min-w-0 px-4 py-4 sm:px-5">
      <div className="mb-2 flex items-center gap-2 text-[#667085]">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#F2F4F7] text-[#475467]">
          {icon}
        </span>
        <span className="truncate text-[13px] font-medium">{label}</span>
      </div>
      <p className="text-[15px] font-semibold text-[#101828]">
        {used}
        <span className="ml-1 text-[13px] font-normal text-[#667085]">/ {limitLabel}</span>
      </p>
      <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-[#F2F4F7]">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            overLimit ? 'bg-[#F79009]' : 'bg-[#12B76A]',
          )}
          style={{ width: `${Math.min(100, Math.max(barWidth, barWidth > 0 ? 6 : 0))}%` }}
        />
      </div>
    </div>
  );
}

export function BillingUsageSummary({
  items,
}: {
  items: Array<{
    key: string;
    icon: ReactNode;
    label: string;
    used: string;
    limitLabel: string;
    percent: number;
  }>;
}) {
  return (
    <div
      className="grid grid-cols-1 divide-y border-y border-[#F2F4F7] sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4"
      data-testid="billing-usage-summary"
    >
      {items.map((item) => (
        <BillingUsageTile
          key={item.key}
          icon={item.icon}
          label={item.label}
          used={item.used}
          limitLabel={item.limitLabel}
          percent={item.percent}
        />
      ))}
    </div>
  );
}

export function BillingUsageList({ children }: { children: ReactNode }) {
  return (
    <div className="divide-y divide-[#F2F4F7]" data-testid="billing-usage-list">
      {children}
    </div>
  );
}

export function BillingUsageGroupRow({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center justify-between gap-4 bg-[#FCFCFD] px-5 py-2.5 sm:px-6">
      <span className="min-w-0 truncate text-[13px] font-medium text-[#101828]">{label}</span>
      <span className="shrink-0 rounded-full bg-[#F2F4F7] px-2 py-0.5 text-[11px] font-medium tabular-nums text-[#667085]">
        {count}
      </span>
    </div>
  );
}

export function BillingUsageRow({
  label,
  used,
  limitLabel,
  percent,
  showBar,
  indented = false,
  overrideInfo,
  valueText,
  overrideIsToggle = false,
}: {
  label: string;
  used: number;
  limitLabel: string;
  percent: number;
  showBar: boolean;
  indented?: boolean;
  overrideInfo?: { planValue: number; overriddenValue: number } | null;
  /** Shown instead of "used / limit" (e.g. "Included", "2 connected"). */
  valueText?: string;
  /** The override switches the feature on/off rather than setting a number. */
  overrideIsToggle?: boolean;
}) {
  const overLimit = showBar && percent >= 100;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 py-2.5 pr-5 sm:pr-6',
        indented ? 'pl-9 sm:pl-10' : 'pl-5 sm:pl-6',
      )}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="min-w-0 truncate text-[13px] text-[#344054]">{label}</span>
        {overrideInfo && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`${label} limit is overridden for this organization`}
                  className="shrink-0 text-[#98A2B3] outline-none transition hover:text-[#667085]"
                >
                  <CircleHelp className="h-3.5 w-3.5" strokeWidth={1.8} />
                </button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent side="top" align="start" sideOffset={6} className="max-w-[220px] text-xs">
                  {overrideIsToggle ? (
                    <>
                      Plan: {overrideInfo.planValue ? 'Included' : 'Not included'} · Overridden to:{' '}
                      {overrideInfo.overriddenValue ? 'Included' : 'Not included'}
                    </>
                  ) : (
                    <>
                      Plan limit: {formatUsageLimit(overrideInfo.planValue)} · Overridden to:{' '}
                      {formatUsageLimit(overrideInfo.overriddenValue)}
                    </>
                  )}
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
      <span
        className={cn(
          'shrink-0 text-[13px] tabular-nums',
          overLimit ? 'font-medium text-[#B54708]' : 'text-[#667085]',
        )}
      >
        {valueText ?? (
          <>
            {used.toLocaleString()}
            <span className="text-[#D0D5DD]"> / </span>
            {limitLabel}
          </>
        )}
      </span>
    </div>
  );
}

/**
 * What the customer will be charged next cycle, with the list price beside it.
 *
 * 🔴 THE STRIKE-THROUGH IS THE RECURRING DISCOUNT, NEVER THE SIGNUP ONE.
 *
 * `difference` is derived server-side from the customer-specific RECURRING
 * amount against list price — resolveBillingTerms refuses to derive it from a
 * past payment, precisely so a one-off launch discount cannot be advertised as
 * a standing reduction. A customer who got money off their first invoice and
 * renews at list price therefore sees a plain figure here, which is the truth.
 */
export function BillingNextPaymentBox({
  amount,
  /**
   * "Next payment" only when the amount is customer-specific. When the server
   * could only offer the plan's list price it passes "Plan price" — the label
   * is what stops an unverified figure reading as a commitment.
   */
  label = 'Next payment',
  /**
   * The plan's list price, struck through above the amount actually charged.
   * Passed ONLY when the two genuinely differ in the customer's favour — the
   * server suppresses it otherwise, so this never advertises a saving that
   * does not exist.
   */
  originalAmount,
  /** e.g. "20% off". Rendered as a badge beside the amount. */
  differenceLabel,
}: {
  amount: ReactNode;
  label?: string;
  originalAmount?: ReactNode;
  differenceLabel?: string;
}) {
  return (
    <div className="text-right sm:text-left">
      <p className="text-[12px] text-[#667085]">{label}</p>
      {/*
        Storefront ordering: what you pay, then what it would have cost, then
        by how much. Reading left to right answers "how much?" before "off
        what?", so the charged figure never competes with the struck one.

        One line, not stacked: a struck price on its own row above reads as a
        price in its own right for the moment before the eye reaches the real
        one. Sharing a baseline with the amount makes it unmistakably a
        reference, which is the whole job of a strike-through.
      */}
      <div className="mt-0.5 flex flex-wrap items-baseline gap-2 sm:justify-start justify-end">
        <p
          className="text-[18px] font-semibold tabular-nums text-[#12B76A]"
          data-testid="billing-charged-amount"
        >
          {amount}
        </p>
        {originalAmount && (
          <span
            className="text-[13px] font-medium tabular-nums text-[#98A2B3] line-through"
            data-testid="billing-list-price"
          >
            {originalAmount}
          </span>
        )}
        {differenceLabel && (
          <span
            className="rounded-md bg-[#ECFDF3] px-1.5 py-0.5 text-[10px] font-semibold text-[#027A48]"
            data-testid="billing-difference-badge"
          >
            {differenceLabel}
          </span>
        )}
      </div>
    </div>
  );
}

export function BillingPlanHero({
  planName,
  statusTone,
  statusLabel,
  description,
  facts,
  action,
}: {
  planName: string;
  statusTone: 'success' | 'warning';
  statusLabel: string;
  description: ReactNode;
  facts?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="p-5 sm:p-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="text-xl font-semibold tracking-tight text-[#101828] sm:text-[22px]">
              {planName}
            </h3>
            <BillingStatusPill tone={statusTone}>{statusLabel}</BillingStatusPill>
          </div>
          <p className="max-w-xl text-[14px] leading-relaxed text-[#667085]">{description}</p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {facts ? (
        <div className="mt-5 flex flex-col gap-4 border-t border-[#F2F4F7] pt-5 sm:flex-row sm:items-end sm:justify-between">
          {facts}
        </div>
      ) : null}
    </div>
  );
}

export function BillingPlanFact({
  label,
  value,
  highlight,
}: {
  label: string;
  value: ReactNode;
  highlight?: boolean;
}) {
  return (
    <div className="min-w-[120px]">
      <p className="text-[12px] text-[#667085]">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-[15px] font-semibold capitalize text-[#101828]',
          highlight && 'text-[#12B76A]',
        )}
      >
        {value}
      </p>
    </div>
  );
}

export function BillingRecentInvoiceRow({
  title,
  invoiceNumber,
  dateLabel,
  amount,
}: {
  title: string;
  invoiceNumber: string;
  dateLabel?: string;
  amount: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-[#E4E7EC] bg-[#FCFCFD] px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-[13px] font-semibold text-[#101828]">{title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="rounded-md bg-[#F2F4F7] px-1.5 py-0.5 text-[11px] font-medium text-[#667085]">
            {invoiceNumber}
          </span>
          {dateLabel ? (
            <span className="text-[11px] text-[#98A2B3]">{dateLabel}</span>
          ) : null}
        </div>
      </div>
      <p className="shrink-0 text-[14px] font-semibold text-[#101828]">{amount}</p>
    </div>
  );
}

export function BillingInvoiceRow({
  title,
  invoiceNumber,
  dateLabel,
  amount,
  statusTone,
  statusLabel,
  onDownload,
}: {
  title: string;
  invoiceNumber: string;
  dateLabel?: string;
  amount: ReactNode;
  statusTone: 'success' | 'warning' | 'neutral' | 'danger';
  statusLabel: string;
  onDownload?: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-[#FAFBFC] sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-[#101828]">{title}</p>
        <p className="mt-0.5 text-[13px] text-[#667085]">
          {invoiceNumber}
          {dateLabel ? ` · ${dateLabel}` : null}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <p className="text-[14px] font-semibold tabular-nums text-[#101828]">{amount}</p>
        <BillingStatusPill tone={statusTone}>{statusLabel}</BillingStatusPill>
        {onDownload ? (
          <button
            type="button"
            onClick={onDownload}
            className="text-[13px] font-medium text-[#667085] transition-colors hover:text-[#101828]"
          >
            Download
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function BillingMetricChip({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  tone?: 'neutral' | 'warning' | 'success';
}) {
  const wrap =
    tone === 'warning'
      ? 'border-[#FEDF89] bg-[#FFFAEB]'
      : tone === 'success'
        ? 'border-[#A6F4C5] bg-brand-subtle'
        : 'border-[#EAECF0] bg-[#F9FAFB]';
  const labelColor =
    tone === 'warning'
      ? 'text-[#B54708]'
      : tone === 'success'
        ? 'text-brand-cta'
        : 'text-[#667085]';
  const valueColor =
    tone === 'warning'
      ? 'text-[#B54708]'
      : tone === 'success'
        ? 'text-brand-cta'
        : 'text-[#101828]';

  return (
    <div className={cn('flex min-w-[132px] flex-col justify-center rounded-xl border px-4 py-3', wrap)}>
      <span className={cn('mb-1 text-[11px] font-medium uppercase tracking-wide', labelColor)}>
        {label}
      </span>
      <span className={cn('text-[15px] font-semibold capitalize', valueColor)}>{value}</span>
    </div>
  );
}

export function BillingPrimaryButton({
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white transition-colors hover:bg-brand-hover disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function BillingOutlineButton({
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-[#E4E7EC] bg-white px-3 text-[13px] font-semibold text-[#344054] transition-colors hover:bg-[#F9FAFB] disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function BillingEmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#F2F4F7] text-[#98A2B3]">
        {icon}
      </div>
      <p className="text-[15px] font-semibold text-[#101828]">{title}</p>
      <p className="mt-1 max-w-sm text-[13px] text-[#667085]">{description}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function BillingIconTile({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand-cta',
        className,
      )}
    >
      {children}
    </div>
  );
}
